import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { useTooltip } from '@/hooks/useTooltip';

/**
 * Кнопка "Ссылка" — вместо window.prompt() выпадающий попап под кнопкой,
 * тот же принцип, что тултип ссылки в Quill (Orchid): поле URL прямо тут,
 * без модального окна поверх страницы. Открывается с уже подставленным
 * href, если курсор внутри существующей ссылки.
 */
export function RichTextLinkButton({ editor, disabled }: { editor: Editor; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tooltip = useTooltip<HTMLButtonElement>('Ссылка');

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onClickOutside = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const openPopover = () => {
    setUrl((editor.getAttributes('link').href as string | undefined) ?? '');
    setOpen((v) => !v);
  };

  const save = () => {
    const trimmed = url.trim();
    if (trimmed) editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
    else editor.chain().focus().unsetLink().run();
    setOpen(false);
  };

  const remove = () => {
    editor.chain().focus().unsetLink().run();
    setOpen(false);
  };

  const active = editor.isActive('link');

  return (
    <div className="rich-text-editor__link" ref={wrapRef}>
      <button
        ref={tooltip.ref}
        type="button"
        aria-label="Ссылка"
        aria-pressed={active}
        disabled={disabled}
        className={`rich-text-editor__btn${active ? ' rich-text-editor__btn--active' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={openPopover}
        {...tooltip.handlers}
      >
        <LinkIcon />
        {tooltip.node}
      </button>

      {open && (
        <div className="rich-text-editor__link-popover">
          <input
            ref={inputRef}
            type="text"
            value={url}
            placeholder="https://…"
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                save();
              }
              if (e.key === 'Escape') setOpen(false);
            }}
          />
          <button type="button" className="btn btn--primary btn--sm" onClick={save}>
            Сохранить
          </button>
          {active && (
            <button type="button" className="btn btn--secondary btn--sm" onClick={remove}>
              Убрать
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LinkIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 17H7a5 5 0 0 1 0-10h2" />
      <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}
